import { Attention, Check, Close, Youtube } from "assets/svg";
import ChangeLanguageButton from "components/theme/ChangeLanguageButton";
import LandingPageButton from "components/theme/LandingPageButton";
import ShareButton from "components/theme/ShareButton";
import { useLogEvent } from "hooks/useLogEvent";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useEventStore } from "store/useEventStore";
import { shareLink } from "utils/utils";
import { useNavigate } from "react-router-dom";
import FilePreview from "components/FilePreview";
import { filesize } from "filesize";
import ProgressBar from "components/ProgressBar";
import { clarity } from "react-microsoft-clarity";
import { useUploader } from "services/file";
import { useTimeToReleaseUploadStore } from "store";

const VIDEO_SIZE_LIMIT = 800000000;

const validateFile = (file: File) => {
  const isSizeValid = file.size <= VIDEO_SIZE_LIMIT;
  return isSizeValid;
};

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const UploadFile = ({
  file,
  onFileStatusChange,
  status,
  hideProgressBar = false,
  errorMessage,
}: {
  file: File;
  onFileStatusChange?: Function;
  status?: String;
  hideProgressBar?: boolean;
  errorMessage?: React.ReactNode;
}) => {
  const { uploadFile } = useUploader();
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const { theme } = useEventStore();
  const { timeToReleaseChoice } = useTimeToReleaseUploadStore();
  const fileProps = useMemo(() => {
    const humanSize = filesize(file.size);

    return { humanSize };
  }, [file]);

  const uploadFileAsync = useCallback(async () => {
    if (uploading) return;

    setUploading(true);

    try {
      await uploadFile(file, (progress) => setProgress(progress), [
        "recorder",
        ...(timeToReleaseChoice ? [timeToReleaseChoice] : []),
      ]);
    } catch (error) {
      console.error(error);

      if (onFileStatusChange) {
        onFileStatusChange(file, "fail");
      }
    }
  }, [file, uploadFile, onFileStatusChange, uploading, timeToReleaseChoice]);

  useEffect(() => {
    if (status === "uploading" && !uploading) {
      uploadFileAsync();
    }
  }, [status, uploading, uploadFileAsync]);

  useEffect(() => {
    if (progress === -1 && onFileStatusChange) {
      onFileStatusChange(file, "completed");
    }
  }, [progress, file, onFileStatusChange]);


  if (status === "completed") {
    return (
      <div className={`w-full flex p-2 mb-1 card ${theme.card}`}>
        <div className="flex-none relative w-16 h-16 overflow-hidden">
          <FilePreview file={file} />
        </div>

        <div className="w-full flex flex-col items-start justify-start px-2 pt-2">
          <span className="line-clamp-1 break-all">{file.name}</span>
          <span className="text-xs text-slate-600 font-century-gothic-bold">
            {fileProps.humanSize}
          </span>

          {!hideProgressBar && (
            <div className="w-full h-5 flex items-end">
              <ProgressBar progress={100} />
            </div>
          )}
        </div>

        <div className="w-20 flex justify-center items-center">
          <Check width={24} height={24} color="#FFF" outline="#15803d" />
        </div>
      </div>
    );
  }

  if (status === "fail") {
    return (
      <div className={`w-full p-2 mb-1 card ${theme.card}`}>
        <div className="w-full flex">
          <div className="flex-none relative w-16 h-16 overflow-hidden">
            <FilePreview file={file} />
          </div>

          <div className="w-full flex flex-grow flex-col items-start justify-start px-2 pt-2">
            <span className="line-clamp-1 break-all">{file.name}</span>
            <span className="text-xs text-slate-600 font-century-gothic-bold">
              {fileProps.humanSize}
            </span>
          </div>

          <div className="px-1 flex justify-center items-center">
            <Attention width={24} height={24} color="#FFF" outline="#D60000" />
          </div>
        </div>

        <p className="text-red-600 text-xs text-left pt-2 font-century-gothic-bold">
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div className={`w-full flex p-2 mb-1 card ${theme.card}`}>
      <div className="flex-none relative w-16 h-16 overflow-hidden">
        <FilePreview file={file} />
      </div>

      <div className="w-full flex flex-col items-start justify-start px-2 pt-2">
        <span className="line-clamp-1 break-all">{file.name}</span>
        <span className="text-xs text-slate-600 font-century-gothic-bold">
          {fileProps.humanSize}
        </span>

        {!hideProgressBar && (
          <div className="w-full h-5 flex items-end">
            <ProgressBar
              progress={progress < 0 ? 100 : progress}
              color={theme.progressBar}
            />
          </div>
        )}
      </div>

      <div className="w-20 flex justify-center items-center"></div>
    </div>
  );
};

const FileItem = ({ file, onRemove }: { file: File; onRemove: Function }) => {
  const { theme } = useEventStore();

  const fileProps = useMemo(() => {
    const humanSize = filesize(file.size);
    return { humanSize };
  }, [file]);

  return (
    <div className={`w-full flex p-2 mb-1 card ${theme.card}`}>
      <div className="flex-none relative w-16 h-16 overflow-hidden">
        <FilePreview file={file} />
      </div>

      <div className="w-full flex flex-col items-start justify-start px-2 pt-2">
        <span className="line-clamp-1 break-all">{file.name}</span>
        <span className="text-xs text-slate-600 font-century-gothic-bold">
          {fileProps.humanSize}
        </span>
      </div>

      <div className="w-8 px-4 flex justify-center items-center">
        <button onClick={() => onRemove(file)}>
          <Close width={24} height={24} color="#000" outline="#000" />
        </button>
      </div>
    </div>
  );
};

export default function Record() {
  const { event, theme, publicUrl } = useEventStore();
  const [files, setFiles] = useState<Array<File>>([]);
  const intl = useIntl();
  const [uploadStatus, setUploadStatus] = useState<any>({});
  const { logShareButton, logUploadFileType, logPageState } = useLogEvent();
  const videoInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { timeToReleaseChoice } = useTimeToReleaseUploadStore();

  const onShareEventLink = useCallback(() => {
    const text = intl.formatMessage({
      id: "message.shareLink",
      defaultMessage: "Compartilhe seus momentos favoritos da festa!",
    });
    shareLink(publicUrl, text, logShareButton);
  }, [intl, publicUrl, logShareButton]);

  const onLandingPageClick = useCallback(() => {
    const capsulaLadingPage = "https://videorecados.capsuladotempo.com";
    window.open(capsulaLadingPage, "_blank");
  }, []);

  const pageState = useMemo(() => {
    const uploadHistoryFound = Object.entries(uploadStatus).length > 0;
    const uploadFinished = Object.values(uploadStatus).every((status) => {
      return status === "completed" || status === "fail";
    });

    if (uploadHistoryFound && uploadFinished) return "completed";
    if (uploadHistoryFound && !uploadFinished) return "uploading";
    if (files.length === 1 && validateFile(files[0])) return "preview";
    if (files.length > 0) return "preview";

    return "initial";
  }, [files, uploadStatus]);

  const handleVideoButton = () => {
    logUploadFileType("video");
    videoInputRef.current?.click();
  };

  const handleFilesSelected = useCallback(async (event: any) => {
    const filesArray = Array.from(event.target.files);

    if (filesArray && filesArray.length > 0) {
      setFiles([filesArray[0] as File]);
      event.target.value = "";
    }
  }, []);

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("recorder", "engaged");
    }
  }, []);

  const uploadSuccess = useMemo(() => {
    const file = files[0];
    if (!file) return false;
    return uploadStatus[file.name] === "completed";
  }, [files, uploadStatus]);

  const uploadFailure = useMemo(() => {
    const file = files[0];
    if (!file) return false;
    return uploadStatus[file.name] === "fail";
  }, [files, uploadStatus]);

  const handleRemove = (file: File) => {
    setFiles((files) => {
      return files.filter((f) => f !== file);
    });
  };

  const handleBackButton = (_event: any) => {
    setFiles([]);
    setUploadStatus({});
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/?code=" + event?.code);
    }
  };

  const handleUploadFiles = async (event: any) => {
    const file = files[0];
    if (file && validateFile(file)) {
      setUploadStatus({ [file.name]: "uploading" });
    }
  };

  const updateFileStatus = useCallback((file: File, status: string) => {
    setUploadStatus((prevStatus: any) => ({
      ...prevStatus,
      [file.name]: status,
    }));
  }, []);

  useEffect(() => {
    const pageStateTitle =
      "record_" + pageState.replace(/([A-Z])/g, "_$1").toLowerCase();
    logPageState(pageStateTitle);
  }, [pageState, logPageState]);

  if (pageState === "preview") {
    const file = files[0];
    const isValid = validateFile(file);
    return (
      <div className="template-container px-3 pt-8 pb-10 flex flex-col items-center">
        <h1 className="text-center text-theme-black">
          <FormattedMessage
            id="title.recorder.upload"
            defaultMessage="Upload"
          />
        </h1>

        <div className="w-full">
          <div className="mb-8">
            {isValid ? (
              <div className="bg-green-200 p-3 card rounded">
                <h6 className="text-green-700 mb-4 font-century-gothic-bold">
                  <FormattedMessage
                    id="subtitle.recorder.filesReadyToUpload"
                    defaultMessage="Arquivo pronto para o envio:"
                  />
                </h6>

                <FileItem file={file} onRemove={handleRemove} />
              </div>
            ) : (
              <div className="bg-red-200 p-3 card rounded">
                <h6 className="text-red-600 mb-4 font-century-gothic-bold">
                  <FormattedMessage
                    id="subtitle.recorder.fileWithError"
                    defaultMessage="Arquivo que não será enviado:"
                  />
                </h6>
                <UploadFile
                  file={file}
                  status={"fail"}
                  errorMessage={
                    <FormattedMessage
                      id="message.recorder.fileSizeError"
                      defaultMessage="Arquivo excede tamanho permitido"
                    />
                  }
                />
              </div>
            )}
          </div>

          <div className="flex mt-12 justify-between">
            <button
              type="button"
              className={`
              font-century-gothic-bold text-white py-3 w-[120px]  flex whitespace-nowrap justify-center items-center rounded-xl ${theme.button}
            `}
              onClick={handleBackButton}
            >
              <FormattedMessage
                id="button.recorder.back"
                defaultMessage="Voltar"
              />
            </button>

            {isValid && (
              <button
                type="button"
                className={`
              font-century-gothic-bold text-white py-3 w-[120px] whitespace-nowrap justify-center items-center rounded-xl ${theme.button}
            `}
                onClick={handleUploadFiles}
              >
                <FormattedMessage
                  id="button.recorder.send"
                  defaultMessage="Enviar"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (pageState === "uploading") {
    const file = files[0];
    return (
      <div className="template-container px-2 pt-8 pb-10 flex flex-col items-center">
        <h1 className="text-center text-theme-black">
          <FormattedMessage
            id="title.recorder.uploading"
            defaultMessage="Enviando"
          />
        </h1>

        <div className={`card ${theme.card} w-5/6 py-10 px-10`}>
          <p className="text-center font-century-gothic-bold">
            <FormattedMessage
              id="message.recorder.uploading"
              defaultMessage="Por favor, não atualize ou feche esta página."
            />
          </p>
        </div>

        <div className="w-full mt-6 px-3 space-y-1">
          <UploadFile
            file={file}
            onFileStatusChange={updateFileStatus}
            status={uploadStatus[file.name]}
            errorMessage={
              uploadStatus[file.name] === "fail" && (
                <FormattedMessage
                  id="message.recorder.uploadError"
                  defaultMessage="Falha ao enviar."
                />
              )
            }
          />
        </div>
      </div>
    );
  }

  if (pageState === "completed") {
    const file = files[0];

    return (
      <div className="template-container px-2 pt-8 pb-10 flex flex-col items-center">
        <h1 className="text-center text-theme-black">
          {uploadFailure ? (
            <FormattedMessage
              id="title.recorder.uploadFailure"
              defaultMessage="Falha no envio"
            />
          ) : (
            <FormattedMessage
              id="title.recorder.completed"
              defaultMessage="Concluído"
            />
          )}
        </h1>

        <div
          className={`card ${theme.card} w-5/6 py-10 flex flex-col items-center`}
        >
          {uploadSuccess && (
            <>
              <p className="text-center">
                <FormattedMessage
                  id="message.recorder.uploadComplete"
                  defaultMessage="Upload concluído"
                />
              </p>

              <p className="text-center font-century-gothic-bold">
                <FormattedMessage
                  id="message.recorder.fileUploaded"
                  defaultMessage="Arquivo enviado com sucesso"
                />
              </p>
            </>
          )}

          {uploadFailure && (
            <>
              <p className="text-center text-red-600 font-century-gothic-bold">
                <FormattedMessage
                  id="message.recorder.uploadFailed"
                  defaultMessage="Falha ao enviar o arquivo"
                />
              </p>

              <p className="text-center text-red-600 font-century-gothic-bold">
                <FormattedMessage
                  id="message.recorder.checkYourConnectionAndTryAgain"
                  defaultMessage="Verifique sua conexão e tente novamente"
                />
              </p>
            </>
          )}

          <button
            type="button"
            className={`font-century-gothic-bold text-white mt-6 py-3 w-[120px] whitespace-nowrap justify-center items-center rounded-xl ${theme.button}`}
            onClick={handleBackButton}
          >
            <FormattedMessage id="button.recorder.ok" defaultMessage="OK!" />
          </button>
        </div>

        <div className="w-full mt-6 px-3">
          <UploadFile
            file={file}
            onFileStatusChange={updateFileStatus}
            status={uploadStatus[file.name]}
            hideProgressBar={true}
            errorMessage={
              uploadStatus[file.name] === "fail" && (
                <FormattedMessage
                  id="message.recorder.uploadError"
                  defaultMessage="Falha ao enviar."
                />
              )
            }
          />
        </div>
      </div>
    );
  }
  // standard case for initial render
  return (
    <div className="template-container py-16 flex flex-col items-center">
      {timeToReleaseChoice === "release-now" ? (
        <>
          <h1 className="text-center text-theme-black">
            <FormattedMessage
              id="title.recorder.shareMoments"
              defaultMessage="Envie sua Mensagem"
            />
          </h1>

          <p className="text-center text-theme-black -mt-6 mb-8 text-3xl font-playfair-display">
            <FormattedMessage
              id="subtitle.recorder.now"
              defaultMessage="Para Agora"
            />
          </p>
        </>
      ) : (
        <>
          <h1 className="text-center text-theme-black">
            <FormattedMessage
              id="title.recorder.shareMoments"
              defaultMessage="Envie sua Mensagem"
            />
          </h1>
          <p className="text-center text-theme-black -mt-6 mb-8 text-3xl font-playfair-display">
            <FormattedMessage
              id="subtitle.recorder.forThe1YearAnniversary"
              defaultMessage="Para as Bodas de 1 Ano"
            />
          </p>
        </>
      )}

      <div className="px-10">
        <div
          className={`flex flex-col items-center pt-10 pb-8 px-6 card ${theme.card}`}
        >
          <p className="text-theme-black">
            <FormattedMessage
              id="message.recorder.instructions"
              values={{
                code: (text) => (
                  <span className="font-century-gothic-bold">{text}</span>
                ),
              }}
              defaultMessage="<code>Abra o aplicativo da câmera </code> do seu dispositivo no modo selfie e grave seu recado de até <code>2 minutos</code>!"
            />
          </p>
          <p className="text-theme-black pt-6">
            <FormattedMessage
              id="message.recorder.uploadLimit"
              values={{
                code: (text) => (
                  <span className="font-century-gothic-bold">{text}</span>
                ),
              }}
              defaultMessage="Limite máximo:"
            />
          </p>

          <p className="text-theme-black pb-6">
            <FormattedMessage
              id="message.recorder.videoSizeLimit"
              values={{
                code: (text) => (
                  <span className="font-century-gothic-bold">{text}</span>
                ),
              }}
              defaultMessage="<code>800MB</code> por vídeo."
            />
          </p>
          <div className="flex items-between">
            <button
              className={`py-1 px-6 ${theme.button} flex items-center gap-4`}
              type="button"
              onClick={handleVideoButton}
            >
              <Youtube
                width={48}
                height={48}
                color={theme.logo}
                outline="#FFF"
              />
              <FormattedMessage
                id="button.recorder.sendVideo"
                defaultMessage="Enviar vídeo"
              />
            </button>
          </div>
          {isIOS() && (
            <p className="mt-8 font-century-gothic-bold text-gray-400 text-sm text-center">
              <FormattedMessage
                id="message.recorder.uploadTip.ios"
                defaultMessage="💡 Para melhor qualidade, selecione um vídeo da fototeca"
              />
            </p>
          )}
        </div>
      </div>

      <form>
        <input
          ref={videoInputRef}
          type="file"
          name="video"
          accept="video/*"
          className="hidden"
          onChange={handleFilesSelected}
        />
      </form>
      <div className="w-full h-full pt-6 px-4 flex justify-start">
        <div>
          <ChangeLanguageButton />
        </div>

        <div className="pl-2">
          <ShareButton onShare={onShareEventLink} />
        </div>

        <div className="pl-2">
          <LandingPageButton onClick={onLandingPageClick} />
        </div>
      </div>
    </div>
  );
}
